import { ParsedUrlQuery } from 'querystring';

import { GetMarketplacePropertySearchQueryVariables } from '__generated__/athena';

import { getAthenaClient } from 'components/api';

import { GeoPoint } from '../map/types';

export interface PropertySearchQuery {
  bedrooms?: number | null;
  bathrooms?: number | null;
  canSelfTour?: boolean | null;
  minListPrice?: number | null;
  maxListPrice?: number | null;
  minSquareFootage?: number | null;
  maxSquareFootage?: number | null;
  hasSingleStory?: boolean | null;
  hasPool?: boolean | null;
  singleFamily?: boolean | null;
  condo?: boolean | null;
  townhome?: boolean | null;
  zoneIds?: string[] | null;
  minYearBuilt?: number | null;
  maxYearBuilt?: number | null;
  vertices?: GeoPoint[] | null;
  market?: string | null;
  noHoas?: boolean | null;
  minLotSize?: number | null;
  maxLotSize?: number | null;
  preview_aid?: string | null;
}

export const getPropertyList = async (
  payload: GetMarketplacePropertySearchQueryVariables,
  page: number,
) => {
  return getAthenaClient().GetMarketplacePropertyList({ ...payload, page });
};

export const getPropertyListAndSearch = async (
  payload: GetMarketplacePropertySearchQueryVariables,
  page = 0,
) => {
  const searchResult = await getAthenaClient().GetMarketplacePropertySearch({ ...payload });
  const listResult = await getPropertyList(payload, page);

  return { searchResult, listResult };
};

export const updateQueryParams = (input: PropertySearchQuery) => {
  const queryParams = buildQueryParams(input);
  window.history.replaceState({}, '', `${window.location.pathname}?${queryParams}`);
};

export const buildQueryParams = (input: PropertySearchQuery): Record<string, any> => {
  const urlParams = new URLSearchParams(window.location.search);
  for (const [key, value] of Object.entries(input)) {
    if (key === 'zoneIds') {
      urlParams.set('zoneIds', value?.join(','));
    } else if (key === 'vertices') {
      value.forEach((vertex: GeoPoint, indx: number) => {
        urlParams.set(`vertex_${indx}`, encodeURI(JSON.stringify(vertex)));
      });
    } else if (key === 'market') {
      urlParams.set('market', value);
    } else if (typeof value === 'boolean') {
      urlParams.set(key, value.toString());
    } else if (value) {
      urlParams.set(key, value.toString());
    } else {
      urlParams.delete(key);
    }
  }
  return urlParams;
};

export const buildVerticesQueryParams = (vertices: GeoPoint[]): Record<string, any> => {
  const urlParams = new URLSearchParams(window.location.search);
  vertices.forEach((vertex, indx) => {
    urlParams.set(`vertex_${indx}`, encodeURI(JSON.stringify(vertex)));
  });
  return urlParams;
};

export const buildAddressPreviewParams = (addressToken: string): Record<string, any> => {
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.set('preview_aid', addressToken);
  return urlParams;
};

export const removeAddressPreviewParams = (): Record<string, any> => {
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.delete('preview_aid');
  return urlParams;
};

// Maps URL param aliases to their canonical PropertySearchQuery field names
const PARAM_ALIASES: Record<string, string> = {
  minBeds: 'bedrooms',
  minBaths: 'bathrooms',
  minPrice: 'minListPrice',
  maxPrice: 'maxListPrice',
  singleStory: 'hasSingleStory',
  mustHavePool: 'hasPool',
};

export const parseQueryParams = (urlParams: ParsedUrlQuery): PropertySearchQuery => {
  const queryParams: Record<string, any> = {};
  queryParams.vertices = [];

  Object.entries(urlParams)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .forEach(([key, value]) => {
      const normalizedKey = PARAM_ALIASES[key] || key;
      if (normalizedKey === 'zoneIds' && typeof value === 'string') {
        queryParams.zoneIds = value.split(',');
      } else if (key.startsWith('vertex_') && typeof value === 'string') {
        queryParams.vertices.push(JSON.parse(decodeURI(value)));
      } else {
        if (!isNaN(Number(value))) {
          queryParams[normalizedKey] = Number(value as string);
        } else if (value === 'true' || value === 'false') {
          queryParams[normalizedKey] = value === 'true';
        } else {
          queryParams[normalizedKey] = value;
        }
      }
    });
  return queryParams;
};
